import type { Ref } from 'vue'
import { debounce, upperFirst } from 'lodash-es'
import { useClipboard } from '@vueuse/core'
import type { IDoctor, IDoctorGroup, ISelectedDoctor } from '~/types'
import type { IAutocompleteValue } from '~/components/PersistentAutocomplete.vue'

export const useDoctors = (doctorGroups: Ref<IDoctorGroup[]>, year: Ref<number>) => {
  const $route = useRoute()
  const $router = useRouter()
  const { copy } = useClipboard()

  const selectedDoctor = ref<ISelectedDoctor | null>(null)
  const isDialogVisible = ref(false)
  const searchText = ref('')
  const searchId = ref<number | null>(null)

  const specialities = computed(() => doctorGroups.value.map(g => ({
    value: g.id,
    text: upperFirst(g.speciality_name),
  })))

  const searchedSpecialities = computed(() => {
    if (searchText.value.length < 3) {
      return doctorGroups.value
    }

    const _searchText = searchText.value.toLowerCase()

    return doctorGroups.value.filter((g) => {
      if (searchId.value) {
        return g.id === searchId.value
      }

      const groupName = g.speciality_name.toLowerCase()

      return groupName.includes(_searchText)
    })
  })

  onMounted(() => {
    selectDoctorByHashId()
  })

  function onOpenRulesClick() {
    openRulesDocument(year.value)
  }

  function setDialogVisible(value: boolean) {
    isDialogVisible.value = value
    setScrollDisabled(value)
  }

  function selectDoctorByHashId() {
    const hash = $route.hash

    if (hash) {
      const numbersInHas = hash.match(/[0-9]/g) || []
      const doctorId = Number(numbersInHas.join('')) // #member-123 -> 123
      const doctorInfo = getDoctorInfoById(doctorId)

      if (!doctorInfo) {
        setHash('')

        return
      }

      selectDoctor(doctorInfo, doctorInfo.speciality, doctorInfo.place)
    }
  }

  function getDoctorInfoById(doctorId: IDoctor['id']): ISelectedDoctor | undefined {
    const group = doctorGroups.value.find(g => g.doctors.find(item => item.id === doctorId))

    if (!group) {
      return
    }

    const doctorIdx = group.doctors.findIndex(item => item.id === doctorId)!

    return {
      ...group.doctors[doctorIdx],
      speciality: group.speciality_name,
      place: doctorIdx + 1,
    }
  }

  function unselectDoctor() {
    setHash('')
    setDialogVisible(false)
  }

  function selectDoctor(doctor: IDoctor, speciality: string, place: number) {
    selectedDoctor.value = { ...doctor, speciality, place }
    setDialogVisible(true)

    scrollToId(`#doctor-${doctor.id}`)
    setHash(`#member-${doctor.id}`)
  }

  function scrollToId(hash: string) {
    setTimeout(() => {
      scrollToHash(hash, { duration: 500, offset: -250 })
    }, 50)
  }

  function setHash(hash: string) {
    if ($route.hash === hash) {
      return
    }

    return $router.replace({ hash })
  }

  function copyUrl() {
    copy(window.location.href)
    useSnackbarStore().open({ text: 'Ссылка скопирована' })
  }

  function onSearch(value: string | null) {
    searchText.value = value?.trim() || ''
  }

  function onSelect(value: IAutocompleteValue | null) {
    searchId.value = value?.value || null
  }

  const debouncedSearch = debounce(onSearch, 250)

  return {
    selectedDoctor,
    isDialogVisible,
    specialities,
    searchedSpecialities,
    onOpenRulesClick,
    unselectDoctor,
    selectDoctor,
    copyUrl,
    onSelect,
    debouncedSearch,
  }
}
